import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, fmtCurrency, STAGES } from "@/lib/api";
import { useCurrencies, currencySymbol } from "@/lib/currency";
import { GripVertical, X } from "lucide-react";
import StageChangeModal from "@/components/StageChangeModal";

// Deals can be in different currencies, so a set of deals can't be summed
// into one number — group by currency and format each total separately.
const sumByCurrency = (deals, field) => {
  const totals = {};
  for (const d of deals) {
    const c = d.currency || "INR";
    totals[c] = (totals[c] || 0) + (d[field] || 0);
  }
  return totals;
};

export default function Pipeline() {
  const currencies = useCurrencies();
  const [deals, setDeals] = useState([]);
  const [dragId, setDragId] = useState(null);
  const [overStage, setOverStage] = useState(null);
  const [pending, setPending] = useState(null); // { deal, toStage } — awaiting remark/shipment form
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");

  const load = () => api.get("/deals").then((r) => setDeals(r.data));
  useEffect(() => { load(); }, []);

  // "Last activity" is the deal's updated_at (bumped on every stage change / edit).
  const activityDate = (d) => (d.updated_at || d.created_at || "").slice(0, 10);
  const inRange = (d) => {
    const day = activityDate(d);
    if (fromDate && !(day && day >= fromDate)) return false;
    if (toDate && !(day && day <= toDate)) return false;
    return true;
  };
  const visibleDeals = deals.filter(inRange);

  // Bucket a deal by how many whole days ago its last activity was.
  // Bands are contiguous: today, yesterday, 2–7 days ago, and older.
  const ACTIVITY_BANDS = [
    { key: "today", label: "Today", test: (n) => n === 0 },
    { key: "yesterday", label: "Yesterday", test: (n) => n === 1 },
    { key: "prev5", label: "Previous 5 days", test: (n) => n >= 2 && n <= 7 },
    { key: "older", label: "More than 7 days", test: (n) => n > 7 },
  ];
  const startOfToday = new Date(); startOfToday.setHours(0, 0, 0, 0);
  const daysAgo = (d) => {
    const day = activityDate(d);
    if (!day) return Infinity; // no activity → oldest band
    const t = new Date(day + "T00:00:00");
    return Math.floor((startOfToday - t) / 86400000);
  };
  const bandOf = (d) => {
    const n = daysAgo(d);
    return ACTIVITY_BANDS.find((b) => b.test(n)) || ACTIVITY_BANDS[ACTIVITY_BANDS.length - 1];
  };

  const byStage = (k) => visibleDeals.filter((c) => c.stage === k).sort((a, b) => (b.fob_value || 0) - (a.fob_value || 0));
  const totalsLabel = (dealsForTotal, field) => {
    const totals = sumByCurrency(dealsForTotal, field);
    const entries = Object.entries(totals);
    if (entries.length === 0) return fmtCurrency(0, "INR", "₹");
    return entries.map(([code, v]) => fmtCurrency(v, code, currencySymbol(code, currencies))).join(" + ");
  };
  const stageFobTotal = (k) => totalsLabel(byStage(k), "fob_value");
  const stageCifTotal = (k) => totalsLabel(byStage(k), "cif_value");
  const stageExworksTotal = (k) => totalsLabel(byStage(k), "exworks_value");
  // Group a stage's deals into activity bands, preserving band order and value sort within each.
  const bandedStage = (k) => {
    const cards = byStage(k);
    return ACTIVITY_BANDS
      .map((b) => ({ band: b, cards: cards.filter((c) => bandOf(c).key === b.key) }))
      .filter((g) => g.cards.length > 0);
  };

  // Carry the current date range into the Leads page when a stage header is clicked.
  const stageLink = (key) => {
    const p = new URLSearchParams({ stage: key });
    if (fromDate) p.set("from", fromDate);
    if (toDate) p.set("to", toDate);
    return `/leads?${p.toString()}`;
  };

  const onDrop = (stage) => {
    if (!dragId) return;
    const c = deals.find((x) => x.id === dragId);
    setDragId(null);
    setOverStage(null);
    if (!c || c.stage === stage) return;
    // Defer the actual move to the stage-change form (remark + shipment on Sample Sent).
    setPending({ deal: c, toStage: stage });
  };

  return (
    <div data-testid="pipeline-page">
      <div className="flex items-center justify-between mb-6 gap-4 flex-wrap">
        <div className="overline" style={{ fontSize: 11 }}>Drag cards between columns to update stage</div>
        <div className="flex items-center gap-4 flex-wrap">
          <div className="flex gap-2 items-center flex-wrap" data-testid="activity-date-filter">
            <span style={{ fontSize: 11, color: "var(--text-2)", fontWeight: 600, letterSpacing: "0.05em", textTransform: "uppercase", whiteSpace: "nowrap" }}>Last activity:</span>
            <input type="date" value={fromDate} max={toDate || undefined} onChange={(e) => setFromDate(e.target.value)} className="input-base" style={{ padding: "6px 10px", fontSize: 13, width: "auto" }} data-testid="filter-from-date" />
            <span style={{ fontSize: 12, color: "var(--text-2)" }}>–</span>
            <input type="date" value={toDate} min={fromDate || undefined} onChange={(e) => setToDate(e.target.value)} className="input-base" style={{ padding: "6px 10px", fontSize: 13, width: "auto" }} data-testid="filter-to-date" />
            {(fromDate || toDate) && (
              <button onClick={() => { setFromDate(""); setToDate(""); }} className="btn-ghost" title="Clear dates" style={{ padding: "4px 6px" }} data-testid="filter-clear-dates">
                <X size={14} />
              </button>
            )}
          </div>
          <div className="font-mono-data" style={{ fontSize: 13, color: "var(--text-2)" }}>
            Pipeline FOB: <strong style={{ color: "var(--primary)" }}>{totalsLabel(visibleDeals.filter((c) => c.stage !== "won" && c.stage !== "lost"), "fob_value")}</strong>
            {" · "}CIF: <strong style={{ color: "var(--primary)" }}>{totalsLabel(visibleDeals.filter((c) => c.stage !== "won" && c.stage !== "lost"), "cif_value")}</strong>
            {" · "}Ex-Works: <strong style={{ color: "var(--primary)" }}>{totalsLabel(visibleDeals.filter((c) => c.stage !== "won" && c.stage !== "lost"), "exworks_value")}</strong>
          </div>
        </div>
      </div>
      <div style={{ display: "grid", gridTemplateColumns: `repeat(${STAGES.length}, minmax(220px, 1fr))`, gap: 16, overflowX: "auto", paddingBottom: 8 }}>
        {STAGES.map((s) => (
          <div
            key={s.key}
            onDragOver={(e) => { e.preventDefault(); setOverStage(s.key); }}
            onDragLeave={() => setOverStage((x) => (x === s.key ? null : x))}
            onDrop={() => onDrop(s.key)}
            className="card-ledger kanban-col"
            style={{ background: s.bg, padding: 12, minHeight: 420, maxHeight: "70vh", overflowY: "auto", outline: overStage === s.key ? `2px dashed ${s.accent}` : "none" }}
            data-testid={`column-${s.key}`}
          >
            <div className="flex items-center justify-between" style={{ padding: "4px 6px 12px" }}>
              <Link
                to={stageLink(s.key)}
                className="flex items-center gap-2"
                style={{ textDecoration: "none", cursor: "pointer" }}
                title={`Open ${s.label} leads${fromDate || toDate ? " for this date range" : ""}`}
                data-testid={`stage-link-${s.key}`}
                onMouseEnter={(e) => { e.currentTarget.querySelector("[data-stage-label]").style.textDecoration = "underline"; }}
                onMouseLeave={(e) => { e.currentTarget.querySelector("[data-stage-label]").style.textDecoration = "none"; }}
              >
                <span style={{ width: 8, height: 8, background: s.accent, borderRadius: 2 }} />
                <span data-stage-label className="font-display" style={{ fontWeight: 700, fontSize: 14, color: s.accent }}>{s.label}</span>
                <span style={{ fontSize: 12, color: "var(--text-2)" }}>· {byStage(s.key).length}</span>
              </Link>
              <div style={{ textAlign: "right" }}>
                <div className="font-mono-data" style={{ fontSize: 11, color: "var(--text-2)" }}>FOB {stageFobTotal(s.key)}</div>
                <div className="font-mono-data" style={{ fontSize: 10, color: "var(--text-2)" }}>CIF {stageCifTotal(s.key)}</div>
                <div className="font-mono-data" style={{ fontSize: 10, color: "var(--text-2)" }}>EXW {stageExworksTotal(s.key)}</div>
              </div>
            </div>

            <div className="flex flex-col gap-2">
              {bandedStage(s.key).map(({ band, cards }) => (
                <div key={band.key} className="flex flex-col gap-2">
                  <div
                    className="overline flex items-center justify-between"
                    style={{ fontSize: 10, color: "var(--text-2)", padding: "2px 6px", marginTop: 2 }}
                    data-testid={`band-${s.key}-${band.key}`}
                  >
                    <span>{band.label}</span>
                    <span style={{ fontWeight: 600 }}>{cards.length}</span>
                  </div>
                  {cards.map((c) => (
                    <div
                      key={c.id}
                      draggable
                      onDragStart={() => setDragId(c.id)}
                      onDragEnd={() => { setDragId(null); setOverStage(null); }}
                      className="card-ledger"
                      style={{ padding: 12, cursor: "grab", background: "#fff", opacity: dragId === c.id ? 0.5 : 1, transition: "opacity 0.15s" }}
                      data-testid={`deal-card-${c.id}`}
                    >
                      <div className="flex items-start gap-2">
                        <GripVertical size={14} style={{ color: "var(--text-2)", marginTop: 1, flexShrink: 0 }} />
                        <div style={{ minWidth: 0, flex: 1 }}>
                          <div style={{ fontWeight: 700, fontSize: 13, lineHeight: 1.25 }}>{c.title || c.requirements || "Untitled lead"}</div>
                          <div style={{ fontSize: 11, color: "var(--text-2)", marginTop: 2 }}>{c.customer_name}</div>
                          <div style={{ fontSize: 12, color: "var(--text-2)", marginTop: 6, lineHeight: 1.4, display: "-webkit-box", WebkitLineClamp: 2, WebkitBoxOrient: "vertical", overflow: "hidden" }}>{c.requirements}</div>
                          <div className="flex items-center justify-between mt-2">
                            <div className="font-mono-data" style={{ fontSize: 12 }}>
                              <div className="flex items-baseline gap-1" style={{ fontWeight: 700, color: s.accent }}>
                                FOB {fmtCurrency(c.fob_value, c.currency, currencySymbol(c.currency, currencies))}
                              </div>
                              <div className="flex items-baseline gap-1" style={{ fontSize: 11, color: "var(--text-2)" }}>
                                CIF {fmtCurrency(c.cif_value, c.currency, currencySymbol(c.currency, currencies))}
                              </div>
                              <div className="flex items-baseline gap-1" style={{ fontSize: 11, color: "var(--text-2)" }}>
                                EXW {fmtCurrency(c.exworks_value, c.currency, currencySymbol(c.currency, currencies))}
                                {c.currency && c.currency !== "INR" && <span style={{ fontWeight: 600 }}>{c.currency}</span>}
                              </div>
                            </div>
                            <div className="flex gap-1">
                              {(c.tags || []).slice(0, 2).map((t) => (
                                <span key={t} className="tag" style={{ fontSize: 9, padding: "1px 6px", background: "var(--surface-2)", borderColor: "var(--border)", color: "var(--text-2)" }}>{t}</span>
                              ))}
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              ))}
              {byStage(s.key).length === 0 && (
                <div style={{ fontSize: 12, color: "var(--text-2)", textAlign: "center", padding: 24, border: "1px dashed var(--border)", borderRadius: 6 }}>
                  Drop deals here
                </div>
              )}
            </div>
          </div>
        ))}
      </div>

      {pending && (
        <StageChangeModal
          deal={pending.deal}
          fromStage={pending.deal.stage}
          toStage={pending.toStage}
          onCancel={() => setPending(null)}
          onDone={() => { setPending(null); load(); }}
        />
      )}
    </div>
  );
}
